import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthValidationError, ResetPasswordRequest, ResetPasswordSuccess } from '../../../store/auth/auth.actions';
import { take } from 'rxjs/internal/operators/take';
import { ValidationErrorComponent } from '../../../components/core/validation-error/validation-error.component';
import { BRANDING } from '../../../core/config/branding';


@Component({
  standalone: true,
  selector: 'app-password-reset',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    RouterLink,
    ValidationErrorComponent
  ],
  templateUrl: './password-reset.html',
  styleUrl: './password-reset.scss',
})
export class PasswordReset {
  protected readonly brand = BRANDING;
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  validationError = signal<string[]>([]);
  constructor() {
    const token = this.route.snapshot.paramMap.get('token') ?? this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.resetForm.patchValue({ token });
    }
  }

  protected readonly resetForm = this.formBuilder.group(
    {
      token: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: this.matchPasswords }
  );

  protected onSubmit(): void {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }
    this.store.dispatch(ResetPasswordRequest({ payload: { token: String(this.resetForm.value.token), password: String(this.resetForm.value.password) } }));
    
    this.actions$.pipe(ofType(ResetPasswordSuccess), take(1)).subscribe(() => {
      this.router.navigate(['/auth/login']);
    });

    this.actions$.pipe(ofType(AuthValidationError), take(1)).subscribe(({ validationError }) => {
      this.validationError.set(validationError);
    });
  }

  private matchPasswords(control: AbstractControl): { passwordMismatch: true } | null {
    const passwordControl = control.get('password');
    const confirmControl = control.get('confirmPassword');
    const password = passwordControl?.value;
    const confirmPassword = confirmControl?.value;

    if (!password || !confirmPassword) {
      if (confirmControl?.hasError('passwordMismatch')) {
        const { passwordMismatch, ...rest } = confirmControl.errors ?? {};
        confirmControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    if (password === confirmPassword) {
      if (confirmControl?.hasError('passwordMismatch')) {
        const { passwordMismatch, ...rest } = confirmControl.errors ?? {};
        confirmControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    confirmControl?.setErrors({ ...(confirmControl.errors ?? {}), passwordMismatch: true });
    return { passwordMismatch: true };
  }
}
